import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';

import { useTranslator } from '../../../../../../components/i18n/useTranslator';
import { getElementId, getTitle } from '../../../../../../../common/elements/utils/elementPropertyUtils';
import { addRootToLabel, updateLabel } from '../../../../../../../common/labels/labelActions';
import Icon from '../../../../../../components/icons/Icon';

import { Button } from '@milanote/roma';
import { ArrowNarrowRight } from '@untitledui/icons';
import { LabelEditContentProps } from '../labelComponentPropTypes';
import { getHighestLabelBoardId, isLabelRootUsed } from '../labelUtils';
import { getUniqueArray } from '../../../../../../../common/utils/arrayUtils';

const LabelMoveButtons: React.FC<LabelEditContentProps> = ({
    label,
    labeledElements,
    userRootPath,
    shareRootPath,
    boardVirtualPathToRootMap,
    contextRootsInCurrentPath,
}) => {
    if (!label || !userRootPath) return null;

    const dispatch = useDispatch();

    const translate = useTranslator('popup', 'labels.edit');
    const highestRootId = getHighestLabelBoardId(label, userRootPath);
    const shareRootIds = shareRootPath.map(getElementId);
    const userRootPathIds = userRootPath.map(getElementId);

    // Find the first board in the path where a label is used either on that board or a descendant of that board that is outside the current path
    const firstDisabledBoardIndex = useMemo(() => {
        if (!boardVirtualPathToRootMap || isEmpty(labeledElements)) return -1;

        // Get a path to root for each board with a label usage on it, and filter to only those that are within the current hierarchy of the label
        const elementPaths = labeledElements
            .map((el) => (el.elementParentId ? boardVirtualPathToRootMap[el.elementParentId] : null))
            .filter((path) => path && path.includes(highestRootId));

        // Find the first board where not all labeled elements share that board as an ancestor
        return userRootPathIds.findIndex((boardId) => !elementPaths?.every((path) => path?.includes(boardId)));
    }, [labeledElements, userRootPath, boardVirtualPathToRootMap]);

    const handleMoveLabel = (newRootBoardId: string) => {
        if (!boardVirtualPathToRootMap || !highestRootId) return;

        const isHighestInCurrentContext = shareRootIds.includes(highestRootId);
        const isNewInCurrentContext = shareRootIds.includes(newRootBoardId);

        // If a label is used within the current context and the user is moving it up out of the current context,
        // then we might need to keep the original rootBoardId if it's used on an element
        if (isHighestInCurrentContext && !isNewInCurrentContext) {
            if (isLabelRootUsed(labeledElements, contextRootsInCurrentPath, highestRootId, boardVirtualPathToRootMap)) {
                dispatch(addRootToLabel(label, newRootBoardId));
                return;
            }
        }

        const newIndex = userRootPathIds.indexOf(newRootBoardId);
        if (newIndex === -1) return;

        const excludedBoardsPath = new Set(userRootPathIds.slice(0, newIndex));

        const rootIdsToRemove = label.rootBoardIds.filter((rootBoardId) => {
            const path = boardVirtualPathToRootMap[rootBoardId];

            // If the current label root sits outside the current label hierarchy, treat it as totally separate and don't remove it
            if (!path || !path?.includes(highestRootId)) return false;

            const lowestSharedBoard = path.find((boardId) => excludedBoardsPath.has(boardId));
            return Boolean(lowestSharedBoard);
        });

        const newRootBoardIds = label.rootBoardIds.filter((id) => !rootIdsToRemove.includes(id));
        newRootBoardIds.push(newRootBoardId);

        dispatch(updateLabel(label.id, { rootBoardIds: getUniqueArray(newRootBoardIds) }));
    };

    return (
        <ul className="LabelsMoveButtons mt-2">
            {userRootPath.map((element, index) => {
                const id = getElementId(element);
                const title = getTitle(element);
                const isLabelOnBoard = id === highestRootId;
                const isDisabled = firstDisabledBoardIndex > -1 && index >= firstDisabledBoardIndex;

                return (
                    <li key={id} className={`flex flex-row`} style={{ marginLeft: `${index * 2}rem` }}>
                        <Button
                            className="flex flex-row items-center"
                            iconLeading={!isLabelOnBoard && ArrowNarrowRight}
                            color={'link-gray'}
                            onClick={() => handleMoveLabel(id)}
                            isDisabled={isDisabled}
                        >
                            <div className="flex w-full gap-1">
                                {isLabelOnBoard && <Icon name="green-tick" />}
                                <span className={`text-left text-sm ml-1`}>
                                    {isLabelOnBoard ? translate('in') : translate('moveTo')}
                                    {'\u00A0'}
                                    <span className="font-bold">{title}</span>
                                </span>
                            </div>
                        </Button>
                    </li>
                );
            })}
        </ul>
    );
};

export default LabelMoveButtons;
